import { Card, CardContent } from '@medikei/ui';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/utils';

interface DashboardCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  variant?: 'default' | 'highlight' | 'success';
  className?: string;
}

export function DashboardCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default',
  className,
}: DashboardCardProps) {
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend.value > 0) return <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />;
    if (trend.value < 0) return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
    return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  const getTrendColor = () => {
    if (!trend) return '';
    if (trend.value > 0) return 'text-emerald-600 dark:text-emerald-400 font-semibold';
    if (trend.value < 0) return 'text-red-600 dark:text-red-400 font-semibold';
    return 'text-muted-foreground';
  };

  return (
    <Card
      className={cn(
        'relative overflow-hidden rounded-2xl border transition-all duration-200 shadow-sm',
        variant === 'highlight' && 'border-2 border-primary/30 bg-primary/5 dark:bg-primary/10 shadow-md',
        variant === 'success' && 'border-2 border-emerald-500/30 bg-emerald-500/5 dark:bg-emerald-500/10',
        className
      )}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-2xl sm:text-3xl font-black mt-1.5 text-foreground tracking-tight truncate">
              {typeof value === 'number' && (title.toLowerCase().includes('saldo') || title.toLowerCase().includes('comissão') || title.toLowerCase().includes('valor'))
                ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                : value}
            </p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            )}
            {trend && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap text-xs">
                {getTrendIcon()}
                <span className={getTrendColor()}>
                  {trend.value > 0 ? '+' : ''}{trend.value}%
                </span>
                <span className="text-muted-foreground">{trend.label}</span>
              </div>
            )}
          </div>
          {icon && (
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 shadow-xs">
              {icon}
            </div>
          )}
        </div>
        {variant === 'highlight' && (
          <div className="absolute top-0 right-0 w-24 h-24 bg-primary/15 rounded-full blur-2xl pointer-events-none -translate-y-1/2 translate-x-1/2" />
        )}
        {variant === 'success' && (
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/15 rounded-full blur-2xl pointer-events-none -translate-y-1/2 translate-x-1/2" />
        )}
      </CardContent>
    </Card>
  );
}